import { useContentfulLocale } from 'hooks/useContentfulLocale';
import { useGatsbyImage } from 'hooks/useGatsbyImage';
import { useExitIntent } from 'hooks/useExitIntent';

import { Layout } from 'components/Layout';
import { Seo } from 'components/Seo';
import { SignupLink } from 'components/Links';
import { Container, HeroContainer, Section, SectionImage } from 'components/Container';
import { BrokerAnimationBlock } from 'components/Blocks/BrokerAnimationBlock';
import { StatsBlock } from 'components/Blocks/StatsBlock';
import { PricingBlock } from 'components/Blocks/PricingBlock';
import { TrustPilot } from 'components/Blocks/TrustPilot';
import { CTANotice } from 'components/Notice';
import { Cards } from 'components/Cards';
import { DividendCalculator as DividendCalculatorComponent } from 'components/Calculator/DividendCalculator';
import { ExitIntentOverlay } from 'components/ExitIntentOverlay';

import { dividendCalculatorCards } from 'page-data/dividend-calculator';

import type { FC } from 'react';
import type { LocalizedPageProps } from '@sharesight/gatsby-plugin-sharesight-localization';

export const DividendCalculator: FC<LocalizedPageProps> = () => {
  const {
    translations: { trackedInvestments, stock },
  } = useContentfulLocale();

  const { showOverlay, handleClose } = useExitIntent('LP');

  return (
    <Layout>
      {showOverlay && <ExitIntentOverlay handleClose={handleClose} utmSource="LP" />}

      <Seo />

      <HeroContainer manageLayout={false}>
        <Section center>
          <h1>Dividend calculator</h1>
          <p>Estimate your monthly dividend income with Sharesight’s dividend calculator.</p>
          <br />
          <br />
        </Section>

        <Section>
          <DividendCalculatorComponent />
        </Section>
      </HeroContainer>
      <CTANotice />

      <Cards cards={dividendCalculatorCards} />
      <Container>
        <Section>
          <h2>Track your dividends with Sharesight</h2>
          <p>
            Automatically track dividends and distributions for more than {trackedInvestments}{' '}
            {stock}s, ETFs and funds worldwide.
          </p>
          <SignupLink asButton showArrowIcon />
        </Section>
        <SectionImage
          image={useGatsbyImage({
            name: 'shared-page-assets/track-dividends',
            alt: 'Track your dividends with Sharesight',
          })}
          boxShadow={false}
        />
      </Container>
      <CTANotice />
      <BrokerAnimationBlock />
      <StatsBlock />
      <PricingBlock />
      <TrustPilot />
      <Container padding={10} background="white">
        <small>
          <strong>Disclaimer:</strong>
          <br />
          <br />
          <u>How the calculation works</u>
          <br />
          <br />
          Calculations are based on limited user inputs and general assumptions, creating
          hypothetical scenarios to illustrate potential dividend outcomes that may not reflect
          actual market conditions, tax implications, or individual circumstances. <br />
          <br />
          <u>Limitations</u>
          <br />
          <br />
          The calculator is a model, not a prediction of dividend amounts or investment return.
          Investing involves risks, including the potential loss of principal. Past performance does
          not guarantee future results. <br />
          You acknowledge and agree that the estimate relies on you entering accurate information
          and on general assumptions. It does not account for fees, taxes, market conditions, or any
          other variable factors. We do not guarantee the accuracy, completeness, or timeliness of
          the calculations and are not responsible for errors, omissions, or results obtained from
          using this calculator. By using the calculator, you acknowledge these hypothetical
          calculations are for illustrative purposes only and agree to this disclaimer. <br />
          The calculator and its content are for informational purposes only, not advice of any
          nature and is not intended to be relied upon for the purposes of making a financial
          decision. You should seek advice from qualified professionals, consider your personal
          financial circumstances, perform your own calculations and decide what is best for you
          according to your own circumstances and objectives before making any investment decisions.
          To the extent permitted by law, we do not assume any responsibility or liability, and you
          waive and release us from all responsibility or liability, arising from or connected with
          your use or reliance on the calculator.
        </small>
      </Container>
    </Layout>
  );
};

export default DividendCalculator;
